import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Clock, Calendar, Tag, Headphones, Video, FileText } from 'lucide-react';

export type MediaType = 'blog' | 'podcast' | 'video';

export interface MediaCardProps {
    id: string;
    title: string;
    type: MediaType;
    description: string;
    coverImage: string;
    date: string;
    duration?: string;
    views?: string;
    author?: {
        name: string;
        avatar?: string;
    };
    tags?: string[];
    href: string;
    className?: string;
    layout?: 'vertical' | 'horizontal';
    size?: 'sm' | 'md' | 'lg';
}

export function MediaCard({
    title,
    type,
    description,
    coverImage,
    date,
    duration,
    views,
    author,
    tags,
    href,
    className = '',
    layout = 'vertical',
    size = 'md',
}: MediaCardProps) {
    // Helper function for type icon
    const getTypeIcon = (mediaType: MediaType) => {
        switch (mediaType) {
            case 'blog':
                return <FileText className="w-4 h-4" />;
            case 'podcast':
                return <Headphones className="w-4 h-4" />;
            case 'video':
                return <Video className="w-4 h-4" />;
            default:
                return null;
        }
    };

    // Generate size-specific styles
    const getSizeStyles = () => {
        switch (size) {
            case 'sm':
                return {
                    height: layout === 'vertical' ? 'h-[320px]' : 'h-24',
                    imageHeight: layout === 'vertical' ? 'h-36' : 'h-24',
                    titleClass: 'text-sm line-clamp-2',
                    descriptionClass: 'text-xs line-clamp-2',
                };
            case 'lg':
                return {
                    height: layout === 'vertical' ? 'h-[480px]' : 'h-40',
                    imageHeight: layout === 'vertical' ? 'h-64' : 'h-40',
                    titleClass: 'text-xl line-clamp-3',
                    descriptionClass: 'text-base line-clamp-3',
                };
            default: // md
                return {
                    height: layout === 'vertical' ? 'h-[400px]' : 'h-32',
                    imageHeight: layout === 'vertical' ? 'h-48' : 'h-32',
                    titleClass: 'text-base line-clamp-2',
                    descriptionClass: 'text-sm line-clamp-2',
                };
        }
    };

    const sizeStyles = getSizeStyles();

    // Horizontal layout
    if (layout === 'horizontal') {
        return (
            <Link to={href} className={`block group ${className}`}>
                <Card className="flex overflow-hidden transition-all hover:shadow-md">
                    <div
                        className={`relative ${sizeStyles.imageHeight} w-1/3 bg-cover bg-center`}
                        style={{ backgroundImage: `url(${coverImage})` }}
                    >
                        {type === 'video' && duration && (
                            <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded">
                                {duration}
                            </div>
                        )}
                    </div>
                    <div className="w-2/3 flex flex-col">
                        <CardHeader className="p-3 pb-0">
                            <div className="flex items-center text-xs text-muted-foreground gap-2 mb-1">
                                <span className="flex items-center gap-1">
                                    {getTypeIcon(type)}
                                    <span className="capitalize">{type}</span>
                                </span>
                                {date && (
                                    <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {date}
                                    </span>
                                )}
                            </div>
                            <CardTitle className={sizeStyles.titleClass}>{title}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 pt-1 pb-0 flex-grow">
                            <CardDescription className={sizeStyles.descriptionClass}>
                                {description}
                            </CardDescription>
                        </CardContent>
                        <CardFooter className="p-3 pt-1 text-xs text-muted-foreground">
                            {author && (
                                <span className="mr-auto">{author.name}</span>
                            )}
                            {duration && type !== 'video' && (
                                <span className="flex items-center gap-1 ml-2">
                                    <Clock className="w-3 h-3" />
                                    {duration}
                                </span>
                            )}
                            {views && (
                                <span className="ml-2">{views} views</span>
                            )}
                        </CardFooter>
                    </div>
                </Card>
            </Link>
        );
    }

    // Vertical layout (default)
    return (
        <Link to={href} className={`block group ${className}`}>
            <Card className={`overflow-hidden h-full transition-all hover:shadow-md flex flex-col ${sizeStyles.height}`}>
                <div className="relative">
                    <div
                        className={`${sizeStyles.imageHeight} w-full bg-cover bg-center`}
                        style={{ backgroundImage: `url(${coverImage})` }}
                    >
                    </div>
                    {type === 'video' && duration && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1 rounded">
                            {duration}
                        </div>
                    )}
                    <div className="absolute top-2 left-2 bg-primary text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        {getTypeIcon(type)}
                        <span className="capitalize">{type}</span>
                    </div>
                </div>
                <CardHeader className="p-4 pb-0">
                    <CardTitle className={sizeStyles.titleClass}>{title}</CardTitle>
                    <div className="flex items-center text-xs text-muted-foreground gap-2 mt-1">
                        {date && (
                            <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {date}
                            </span>
                        )}
                        {duration && type !== 'video' && (
                            <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {duration}
                            </span>
                        )}
                        {views && (
                            <span className="ml-auto">{views} views</span>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="p-4 pt-2 flex-grow">
                    <CardDescription className={sizeStyles.descriptionClass}>
                        {description}
                    </CardDescription>
                </CardContent>
                {(author || tags) && (
                    <CardFooter className="p-4 pt-0 mt-auto">
                        {author && (
                            <div className="flex items-center gap-2 text-xs">
                                {author.avatar && (
                                    <div className="w-5 h-5 rounded-full overflow-hidden">
                                        <img src={author.avatar} alt={author.name} className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <span>{author.name}</span>
                            </div>
                        )}
                        {tags && tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 ml-auto">
                                {tags.slice(0, 2).map(tag => (
                                    <span
                                        key={tag}
                                        className="text-xs px-2 py-0.5 bg-muted rounded-full flex items-center gap-1"
                                    >
                                        <Tag className="w-3 h-3" />
                                        {tag}
                                    </span>
                                ))}
                                {tags.length > 2 && (
                                    <span className="text-xs">+{tags.length - 2}</span>
                                )}
                            </div>
                        )}
                    </CardFooter>
                )}
            </Card>
        </Link>
    );
} 